import React, { memo, useCallback, useRef } from 'react';
import { View, FlatList, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TagListSkeleton } from './TagListSkeleton';
import { TagListItem } from './TagListItem';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagListProps {
  tags: CorkBoardTag[];
  isLoading: boolean;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  onMoveUp?: (tagId: number) => void;
  onMoveDown?: (tagId: number) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const TagList: React.FC<TagListProps> = memo(
  ({
    tags,
    isLoading,
    onToggleEnabled,
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
    onRefresh,
    isRefreshing,
  }) => {
    const { redesign } = useAdminColors();
    const tagsLengthRef = useRef(tags.length);
    tagsLengthRef.current = tags.length;

    const renderItem = useCallback(
      ({ item, index }: { item: CorkBoardTag; index: number }) => (
        <TagListItem
          tag={item}
          isFirst={index === 0}
          isLast={index === tagsLengthRef.current - 1}
          onToggleEnabled={onToggleEnabled}
          onEdit={onEdit}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
        />
      ),
      [onToggleEnabled, onEdit, onDelete, onMoveUp, onMoveDown],
    );

    const keyExtractor = useCallback((item: CorkBoardTag) => String(item.id), []);

    if (isLoading && tags.length === 0) {
      return <TagListSkeleton />;
    }

    if (!isLoading && tags.length === 0) {
      return (
        <View style={styles.centered}>
          <SVG_COMPONENTS.EMPTY_MESSAGE width={48} height={48} color={redesign.text.subtle} />
          <Text style={[styles.emptyTitle, { color: redesign.text.primary }]}>No tags yet</Text>
          <Text style={[styles.emptySubtitle, { color: redesign.text.secondary }]}>
            Create your first tag to help residents categorize their posts.
          </Text>
        </View>
      );
    }

    return (
      <FlatList
        data={tags}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        onRefresh={onRefresh}
        refreshing={isRefreshing ?? false}
      />
    );
  },
);

TagList.displayName = 'TagList';

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    paddingTop: 4,
    paddingBottom: 16,
    flexGrow: 1,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    minHeight: 200,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 300,
  },
});
